import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarDays, Check, Layers, MapPin, Plus, Sparkles, User } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../Redux/Hooks";
import { submitEventFormAPI } from "../../../Redux/ApiCalls/Dashboard/EventAPI";
import { showErrorToast, showSuccessToast } from "../../atoms/Utlis/Toast";
import { EnteredSubEventType } from "../../../Data/event.dto";
import SubEventScreen from "./SubEventScreen";
import ReviewEventDetails from "./ReviewEventDetails";
import { getWhiteLableObject } from "../../../config/whitelable";

const STEPS = [
    { id: 1, label: "Your details", hint: "Who & what" },
    { id: 2, label: "Day schedule", hint: "When & where" },
    { id: 3, label: "Review", hint: "Confirm & send" },
] as const;

const fieldClass =
    "w-full h-[52px] px-4 rounded-2xl bg-white border border-black/10 text-[#171717] font-medium placeholder:text-black/35 placeholder:font-normal focus:outline-none focus:border-[#CE56B0]/50 focus:ring-4 focus:ring-[#CE56B0]/10 transition";

const fieldWrapClass = "flex flex-col gap-2 min-w-0";
const labelClass = "text-xs font-bold uppercase tracking-wide text-black/45 leading-none";

const EventFormNew = () => {
    const [step, setStep] = useState(1);
    const [customerName, setCustomerName] = useState("");
    const [customerPhoneNumber, setCustomerPhoneNumber] = useState("");
    const [eventName, setEventName] = useState("");
    const [locationList, setLocationList] = useState<string[]>([]);
    const [subEvents, setSubEvents] = useState<EnteredSubEventType[]>([]);
    const [showError, setShowError] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [currentSubEvent, setCurrentSubEvent] =
        useState<EnteredSubEventType | null>(null);
    const [formToken, setFormToken] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [showErrorInReview, setShowErrorInReview] = useState(false);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const { loading, success } = useAppSelector((state) => state.extra);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value.length <= 10) {
            setCustomerPhoneNumber(event.target.value);
        }
    };

    useEffect(() => {
        if (params["data"]) setFormToken(params["data"]);
        else {
            showErrorToast("Invalid Link, Please Contact your studio owner.");
            navigate("/auth/login");
        }
    }, [params]);

    useEffect(() => {
        if (success) {
            showSuccessToast("Event created successfully.");
            setIsFormSubmitted(true);
        }
    }, [success]);

    const validCustomerName = (n: string) => n.length > 0 && n.length <= 30;
    const validPhoneNumber = (num: string) => /^[0-9]{10}$/.test(num);
    const validEventName = (n: string) => n.length > 0 && n.length <= 40;
    const validSubEvents = () => subEvents.length > 0;

    const isFormValid = () =>
        validCustomerName(customerName) &&
        validPhoneNumber(customerPhoneNumber) &&
        validEventName(eventName) &&
        validSubEvents();

    const handleSubmit = () => {
        if (isFormValid()) {
            setShowErrorInReview(false);
            dispatch(
                submitEventFormAPI({
                    token: formToken,
                    customer_name: customerName,
                    sub_events: subEvents,
                    event_name: eventName,
                    phone_number: "+91" + customerPhoneNumber,
                })
            )
                .unwrap()
                .then(() => { })
                .catch((err) => {
                    setErrorMessage(
                        err.message === "Link expired." ? "Your Link has been expired" : ""
                    );
                    setIsFormSubmitted(true);
                });
        } else {
            setShowError(true);
            setShowErrorInReview(true);
        }
    };

    const goToPreview = () => {
        if (isFormValid()) {
            setShowErrorInReview(false);
            setStep(3);
        } else {
            setShowError(true);
        }
    };

    const handleAddSubEvents = (subEvent: EnteredSubEventType) => {
        const updated = subEvents.filter((e) => e.id !== subEvent.id);
        setSubEvents([...updated, subEvent]);
        setStep(3);
    };

    const handleRemoveSubEvents = (event: EnteredSubEventType) => {
        const updated = subEvents.filter((e) => e.id !== event.id);
        setSubEvents(updated);
    };

    useEffect(() => {
        if (subEvents.length === 0 && step === 3) {
            setStep(1);
        }
    }, [subEvents, step]);

    const brandLogo = `/logos/${getWhiteLableObject().logo.filename}`;

    return (
        <div className="event_sub_form relative w-full min-h-screen flex flex-col items-center overflow-x-hidden">
            {/* Atmospheric background */}
            <div className="pointer-events-none fixed inset-0 -z-10 bg-[#FBF8F4]">
                <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full bg-[#FFC951]/25 blur-3xl" />
                <div className="absolute top-1/3 -left-20 h-80 w-80 rounded-full bg-[#CE56B0]/15 blur-3xl" />
                <div className="absolute bottom-0 right-1/4 h-64 w-64 rounded-full bg-[#35B7C8]/10 blur-3xl" />
            </div>

            {/* Header */}
            <header className="w-full border-b border-black/5 bg-white/70 backdrop-blur-md">
                <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-3 px-5 py-6 text-center sm:px-8">
                    <img
                        src={brandLogo}
                        alt="Studio logo"
                        onClick={() => window.open("https://kwikmemories.com", "_blank")}
                        className="h-14 w-[200px] cursor-pointer object-contain sm:h-16"
                    />
                    <div className="inline-flex items-center gap-1.5 rounded-full border border-[#CE56B0]/20 bg-white/80 px-3 py-1 text-[11px] font-semibold tracking-wide text-[#C94DAA]">
                        <Sparkles size={12} />
                        Event inquiry
                    </div>
                    <h1 className="font-[Urbanist,sans-serif] text-2xl font-extrabold tracking-tight text-[#171717] sm:text-3xl">
                        {step === 1 && "Tell us about your event"}
                        {step === 2 && "Add a day to your schedule"}
                        {step === 3 && (isFormSubmitted ? "All set" : "Review & submit")}
                    </h1>
                    <p className="max-w-md text-sm font-medium leading-relaxed text-black/55">
                        {step === 1 &&
                            "Share a few details so your studio can plan the perfect shoot. Takes about 2 minutes."}
                        {step === 2 &&
                            "Each day needs a name, date, time, duration, and venue. You can add more days later."}
                        {step === 3 &&
                            !isFormSubmitted &&
                            "Double-check everything looks right, then send it to your studio."}
                        {step === 3 &&
                            isFormSubmitted &&
                            "Your studio has received your inquiry."}
                    </p>
                </div>
            </header>

            {/* Stepper */}
            {!isFormSubmitted && (
                <nav className="mx-auto mt-8 w-full max-w-5xl px-5 sm:px-8" aria-label="Form progress">
                    <ol className="flex items-start justify-between gap-2">
                        {STEPS.map((s, index) => {
                            const done = step > s.id;
                            const active = step === s.id;
                            return (
                                <li key={s.id} className="relative flex flex-1 flex-col items-center text-center">
                                    {index < STEPS.length - 1 && (
                                        <span
                                            className={`absolute left-[calc(50%+22px)] right-[calc(-50%+22px)] top-5 hidden h-0.5 sm:block ${step > s.id ? "background-linear" : "bg-black/10"
                                                }`}
                                            aria-hidden
                                        />
                                    )}
                                    <span
                                        className={`relative z-[1] flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold transition sm:h-11 sm:w-11 ${done || active
                                            ? "background-linear text-white shadow-md shadow-[#CE56B0]/25"
                                            : "border border-black/10 bg-white text-black/40"
                                            }`}
                                    >
                                        {done ? <Check size={18} strokeWidth={2.5} /> : s.id}
                                    </span>
                                    <span
                                        className={`mt-2 text-xs font-bold sm:text-sm ${active || done ? "text-[#171717]" : "text-black/40"
                                            }`}
                                    >
                                        {s.label}
                                    </span>
                                    <span className="mt-0.5 hidden text-[11px] font-medium text-black/45 sm:block">
                                        {s.hint}
                                    </span>
                                </li>
                            );
                        })}
                    </ol>
                </nav>
            )}

            {/* Main content */}
            <main className="mx-auto mb-16 mt-8 w-full max-w-5xl flex-1 px-4 sm:px-8">
                {(step === 1 || step === 2) && (
                    <div className="rounded-[24px] border border-black/[0.06] bg-white/90 p-5 shadow-[0_20px_50px_rgba(23,23,23,0.06)] backdrop-blur-sm sm:p-8">
                        {step === 1 && (
                            <div className="flex flex-col gap-7">
                                {/* About you */}
                                <section className="flex flex-col gap-4">
                                    <div className="flex items-start gap-3">
                                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl background-linear text-white">
                                            <User size={16} />
                                        </span>
                                        <div>
                                            <h2 className="font-[Urbanist,sans-serif] text-lg font-bold text-[#171717]">
                                                About you
                                            </h2>
                                            <p className="mt-0.5 text-sm font-medium text-black/50">
                                                So your studio can reach you about this booking.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-2">
                                        <div className={fieldWrapClass}>
                                            <label className={labelClass}>
                                                Your name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g. Priya Sharma"
                                                className={fieldClass}
                                                value={customerName}
                                                onChange={(e) => setCustomerName(e.target.value)}
                                            />
                                            {showError && !validCustomerName(customerName) && (
                                                <p className="text-sm text-red-500">
                                                    Please enter your name (max 30 characters).
                                                </p>
                                            )}
                                        </div>
                                        <div className={fieldWrapClass}>
                                            <label className={labelClass}>
                                                Phone number <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-black/40">
                                                    +91
                                                </span>
                                                <input
                                                    type="tel"
                                                    inputMode="numeric"
                                                    placeholder="10-digit mobile number"
                                                    className={`${fieldClass} pl-12`}
                                                    value={customerPhoneNumber}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                            {showError && !validPhoneNumber(customerPhoneNumber) && (
                                                <p className="text-sm text-red-500">
                                                    Enter a valid 10-digit mobile number.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </section>

                                <div className="h-px bg-black/5" />

                                {/* Event */}
                                <section className="flex flex-col gap-4">
                                    <div className="flex items-start gap-3">
                                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl background-linear text-white">
                                            <Sparkles size={16} />
                                        </span>
                                        <div>
                                            <h2 className="font-[Urbanist,sans-serif] text-lg font-bold text-[#171717]">
                                                What are we celebrating?
                                            </h2>
                                            <p className="mt-0.5 text-sm font-medium text-black/50">
                                                A short name for the overall event (Wedding, Birthday, etc.).
                                            </p>
                                        </div>
                                    </div>
                                    <div className={`max-w-xl ${fieldWrapClass}`}>
                                        <label className={labelClass}>
                                            Event name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Sharma Wedding 2026"
                                            className={fieldClass}
                                            value={eventName}
                                            onChange={(e) => setEventName(e.target.value)}
                                        />
                                        {showError && !validEventName(eventName) && (
                                            <p className="text-sm text-red-500">
                                                Please enter an event name.
                                            </p>
                                        )}
                                    </div>
                                </section>

                                <div className="h-px bg-black/5" />

                                {/* Day schedules */}
                                <section className="flex flex-col gap-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-start gap-3">
                                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl background-linear text-white">
                                                <Layers size={16} />
                                            </span>
                                            <div>
                                                <h2 className="font-[Urbanist,sans-serif] text-lg font-bold text-[#171717]">
                                                    Day schedules{" "}
                                                    <span className="font-semibold text-black/40">
                                                        ({subEvents.length})
                                                    </span>{" "}
                                                    <span className="text-red-500">*</span>
                                                </h2>
                                                <p className="mt-0.5 text-sm font-medium text-black/50">
                                                    Add each shoot day — engagement, wedding day, reception, and more.
                                                </p>
                                            </div>
                                        </div>
                                        {subEvents.length > 0 && subEvents.length < 10 && (
                                            <button
                                                type="button"
                                                className="hidden shrink-0 items-center gap-1.5 rounded-xl background-linear px-3.5 py-2 text-sm font-bold text-white shadow-md shadow-[#CE56B0]/20 transition hover:-translate-y-0.5 sm:inline-flex"
                                                onClick={() => {
                                                    setCurrentSubEvent(null);
                                                    setStep(2);
                                                }}
                                            >
                                                <Plus size={16} />
                                                Add day
                                            </button>
                                        )}
                                    </div>

                                    {subEvents.length === 0 ? (
                                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-[#CE56B0]/30 bg-gradient-to-br from-[#FFC951]/10 to-[#CE56B0]/5 px-5 py-10 text-center">
                                            <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#CE56B0]/15 bg-white text-[#C94DAA]">
                                                <CalendarDays size={22} />
                                            </span>
                                            <p className="font-[Urbanist,sans-serif] text-base font-bold text-[#171717]">
                                                No days added yet
                                            </p>
                                            <p className="max-w-sm text-sm font-medium leading-relaxed text-black/50">
                                                Start with your first shoot day. You can add as many as you need.
                                            </p>
                                            <button
                                                type="button"
                                                className="mt-1 inline-flex items-center gap-1.5 rounded-xl background-linear px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-[#CE56B0]/25"
                                                onClick={() => {
                                                    setCurrentSubEvent(null);
                                                    setStep(2);
                                                }}
                                            >
                                                <Plus size={16} />
                                                Add your first day
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                            {subEvents.map((event, index) => (
                                                <button
                                                    type="button"
                                                    key={event.id}
                                                    className="group flex flex-col gap-2 rounded-2xl border border-black/8 bg-[#FAFAFA] p-4 text-left transition hover:border-[#CE56B0]/35 hover:bg-white hover:shadow-lg hover:shadow-[#CE56B0]/10"
                                                    onClick={() => {
                                                        setCurrentSubEvent(event);
                                                        setStep(2);
                                                    }}
                                                >
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="rounded-full bg-[#CE56B0]/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#C94DAA]">
                                                            Day {index + 1}
                                                        </span>
                                                        <span
                                                            className="flex h-7 w-7 items-center justify-center rounded-full text-lg text-black/35 transition hover:bg-black/5 hover:text-[#171717]"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveSubEvents(event);
                                                            }}
                                                            role="button"
                                                            aria-label="Remove day"
                                                        >
                                                            ×
                                                        </span>
                                                    </div>
                                                    <p className="font-[Urbanist,sans-serif] text-[15px] font-bold text-[#171717]">
                                                        {event.sub_event_name}
                                                    </p>
                                                    <div className="flex flex-col gap-1 text-xs font-medium text-black/50">
                                                        <span className="inline-flex items-center gap-1.5">
                                                            <CalendarDays size={12} />
                                                            {event.starting_date}
                                                            {event.starting_time
                                                                ? ` · ${String(event.starting_time).slice(0, 5)}`
                                                                : ""}
                                                        </span>
                                                        {event.sub_event_location ? (
                                                            <span className="inline-flex items-center gap-1.5">
                                                                <MapPin size={12} />
                                                                <span className="truncate">{event.sub_event_location}</span>
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    <span className="text-[11px] font-semibold text-[#C94DAA]/80 group-hover:text-[#C94DAA]">
                                                        Tap to edit
                                                    </span>
                                                </button>
                                            ))}
                                            {subEvents.length < 10 && (
                                                <button
                                                    type="button"
                                                    className="flex min-h-[120px] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-[#CE56B0]/28 bg-gradient-to-br from-[#FFC951]/5 to-[#CE56B0]/5 p-4 text-[#C94DAA] transition hover:border-[#CE56B0]/45 sm:hidden"
                                                    onClick={() => {
                                                        setCurrentSubEvent(null);
                                                        setStep(2);
                                                    }}
                                                >
                                                    <Plus size={20} />
                                                    <span className="text-sm font-bold">Add another day</span>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {showError && !validSubEvents() && (
                                        <p className="text-sm text-red-500">
                                            Please add at least one day schedule to continue.
                                        </p>
                                    )}
                                </section>

                                <div className="flex justify-center pt-2">
                                    <button
                                        type="button"
                                        className="w-full max-w-xs rounded-2xl background-linear px-8 py-3.5 text-base font-bold text-white shadow-lg shadow-[#CE56B0]/25 transition hover:-translate-y-0.5 sm:w-auto"
                                        onClick={goToPreview}
                                    >
                                        Continue to review
                                    </button>
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <SubEventScreen
                                locationList={locationList}
                                setLocationList={setLocationList}
                                currentLength={subEvents.length}
                                addSubEvent={handleAddSubEvents}
                                currentSubEvent={currentSubEvent}
                                onClose={() => setStep(1)}
                            />
                        )}
                    </div>
                )}

                {step === 3 && (
                    <ReviewEventDetails
                        customerName={customerName}
                        customerPhoneNumber={customerPhoneNumber}
                        eventName={eventName}
                        setStep={setStep}
                        subEvents={subEvents}
                        setCurrentSubEvent={setCurrentSubEvent}
                        handleRemoveSubEvents={handleRemoveSubEvents}
                        handleSubmit={handleSubmit}
                        loading={loading}
                        isFormSubmitted={isFormSubmitted}
                        errorMessage={errorMessage}
                        showError={showErrorInReview}
                        validCustomerName={validCustomerName}
                        validPhoneNumber={validPhoneNumber}
                        validEventName={validEventName}
                        validSubEvents={validSubEvents}
                        setCustomerName={setCustomerName}
                        setCustomerPhoneNumber={setCustomerPhoneNumber}
                        setEventName={setEventName}
                        handleChange={handleChange}
                    />
                )}
            </main>
        </div>
    );
};

export default EventFormNew;
